import { submitFeedback } from '../analytics.js';

export interface OpenFeedbackModalOpts {
  route: string;
  parent?: HTMLElement;
}

const FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif';
const FELT = '#0d2c1d';
const GOLD = '#d4af37';
const GOLD_LIGHT = '#f4d35e';
const MUTED = '#8a99a3';
const BODY = '#aebfc7';

export function openFeedbackModal(opts: OpenFeedbackModalOpts): void {
  const parent = opts.parent ?? document.body;

  const overlay = document.createElement('div');
  overlay.setAttribute('data-casino-feedback-modal', '');
  overlay.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:9999',
    'display:flex', 'align-items:center', 'justify-content:center',
    'background:rgba(0,0,0,0.65)',
    `font-family:${FONT}`,
  ].join(';');

  const card = document.createElement('div');
  card.style.cssText = [
    `background:${FELT}`, `color:${BODY}`,
    'width:min(92vw,420px)', 'border-radius:12px',
    `border:2px solid ${GOLD}`,
    'padding:18px 20px 16px',
    'box-shadow:0 16px 48px rgba(0,0,0,0.7)',
    'display:flex', 'flex-direction:column', 'gap:14px',
  ].join(';');

  const title = document.createElement('div');
  title.textContent = 'Send feedback';
  title.style.cssText = [
    'font-size:18px', 'font-weight:bold', `color:${GOLD_LIGHT}`,
    'text-align:center', 'letter-spacing:0.5px',
  ].join(';');

  const subtitle = document.createElement('div');
  subtitle.textContent = 'FEEDBACK';
  subtitle.style.cssText = [
    'font-size:11px', 'font-weight:bold', `color:${MUTED}`,
    'text-align:center', 'letter-spacing:1.5px', 'margin-top:-10px',
  ].join(';');

  const kindRow = document.createElement('div');
  kindRow.style.cssText = 'display:flex;gap:8px';
  const featureBtn = makeKindButton('Feature', true);
  const bugBtn = makeKindButton('Bug', false);
  kindRow.append(featureBtn, bugBtn);
  let kind: 'feature' | 'bug' = 'feature';
  featureBtn.addEventListener('click', () => { kind = 'feature'; setSelected(featureBtn, true); setSelected(bugBtn, false); });
  bugBtn.addEventListener('click', () => { kind = 'bug'; setSelected(bugBtn, true); setSelected(featureBtn, false); });

  const textarea = document.createElement('textarea');
  textarea.placeholder = 'What did you have in mind?';
  textarea.maxLength = 2000;
  textarea.rows = 6;
  textarea.style.cssText = [
    'width:100%', 'box-sizing:border-box',
    'background:rgba(0,0,0,0.35)', `color:${BODY}`,
    `border:1px solid ${GOLD}`, 'border-radius:8px',
    'padding:10px 12px', `font:13px ${FONT}`,
    'resize:vertical', 'min-height:120px',
    'outline:none',
  ].join(';');
  textarea.addEventListener('focus', () => { textarea.style.borderColor = GOLD_LIGHT; });
  textarea.addEventListener('blur', () => { textarea.style.borderColor = GOLD; });

  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;justify-content:flex-end;gap:8px;margin-top:2px';
  const cancel = makeActionButton('Cancel', false);
  const submit = makeActionButton('Send', true);
  actions.append(cancel, submit);

  // Phaser 3 attaches mousedown / mouseup / touchstart / touchend /
  // touchcancel listeners to `window` (default `inputWindowEvents: true`)
  // and processes any event whose target isn't the canvas as a game-area
  // pointer event — meaning a tap on this modal would bubble up to
  // window and place a bet on whatever bet zone sits under the tap
  // coordinates. Stop the events at the overlay so they never reach
  // Phaser's window-level handlers — this also covers the post-close
  // iOS ghost-tap shield window, since the overlay stays in the DOM
  // for ~320ms after close (see below) and keeps swallowing events.
  const swallow = (e: Event) => e.stopPropagation();
  const SWALLOWED_EVENTS = [
    'pointerdown', 'pointerup', 'pointermove', 'pointercancel',
    'mousedown', 'mouseup', 'mousemove',
    'touchstart', 'touchend', 'touchmove', 'touchcancel',
    'click',
  ] as const;
  for (const type of SWALLOWED_EVENTS) overlay.addEventListener(type, swallow);

  // Hide the modal visually but keep the overlay in the DOM as an invisible
  // shield for ~320ms before removal — iOS Safari synthesizes a ghost click
  // ~300ms after touchend, and if the overlay is gone by then the click falls
  // through to the Phaser canvas underneath and registers as a stray bet.
  let closing = false;
  const close = () => {
    if (closing) return;
    closing = true;
    overlay.style.background = 'transparent';
    card.style.display = 'none';
    setTimeout(() => {
      overlay.remove();
    }, 320);
  };
  cancel.addEventListener('click', close);
  overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(); });

  submit.addEventListener('click', async () => {
    const body = textarea.value.trim();
    if (!body) { textarea.focus(); return; }
    submit.textContent = 'Sending…';
    submit.disabled = true;
    try {
      await submitFeedback(kind, body, opts.route);
      submit.textContent = 'Thanks!';
      setTimeout(close, 700);
    } catch {
      submit.textContent = 'Try again';
      submit.disabled = false;
    }
  });

  card.append(title, subtitle, kindRow, textarea, actions);
  overlay.append(card);
  parent.append(overlay);
  setTimeout(() => textarea.focus(), 0);
}

function makeKindButton(label: string, selected: boolean): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.textContent = label;
  b.style.cssText = [
    'flex:1', 'padding:10px', 'border-radius:8px',
    'cursor:pointer',
    `font:bold 13px ${FONT}`,
    'letter-spacing:0.5px',
    'transition:background 120ms,color 120ms',
  ].join(';');
  setSelected(b, selected);
  return b;
}

function setSelected(b: HTMLButtonElement, selected: boolean): void {
  if (selected) {
    b.style.background = GOLD;
    b.style.color = FELT;
    b.style.border = `1px solid ${GOLD}`;
  } else {
    b.style.background = 'transparent';
    b.style.color = BODY;
    b.style.border = `1px solid ${GOLD}`;
  }
}

function makeActionButton(label: string, primary: boolean): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.textContent = label;
  b.style.cssText = [
    'padding:10px 18px', 'border-radius:8px', 'cursor:pointer',
    `font:bold 13px ${FONT}`,
    'letter-spacing:0.5px',
    primary
      ? `background:${GOLD};color:${FELT};border:1px solid ${GOLD}`
      : `background:transparent;color:${BODY};border:1px solid ${GOLD}`,
  ].join(';');
  return b;
}
